import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface SpeedStats {
  requestToApprovalHours: number | null;
  approvalToPaidHours: number | null;
  requestToPaidAvgHours: number | null;
  requestToPaidMedianHours: number | null;
  etbPaid: number;
  paidCount: number;
}

export interface PaymentSpeedKPI {
  current: SpeedStats;
  previous: SpeedStats;
  currentLabel: string;
  previousLabel: string;
}

const HOUR = 1000 * 60 * 60;

const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

const median = (xs: number[]) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};

const diffHours = (from?: string | null, to?: string | null) => {
  if (!from || !to) return null;
  const d = (new Date(to).getTime() - new Date(from).getTime()) / HOUR;
  return Number.isFinite(d) && d >= 0 ? d : null;
};

type Row = {
  submitted_at: string | null;
  approved_at: string | null;
  paid_at: string | null;
  created_at: string;
  total_amount: number | null;
  currency: string | null;
};

const buildStats = (rows: Row[]): SpeedStats => {
  const reqToApp: number[] = [];
  const appToPaid: number[] = [];
  const reqToPaid: number[] = [];
  let etbPaid = 0;

  rows.forEach((r) => {
    const requestedAt = r.submitted_at ?? r.created_at;
    const a = diffHours(requestedAt, r.approved_at);
    if (a !== null) reqToApp.push(a);
    const b = diffHours(r.approved_at, r.paid_at);
    if (b !== null) appToPaid.push(b);
    const c = diffHours(requestedAt, r.paid_at);
    if (c !== null) reqToPaid.push(c);
    if ((r.currency ?? 'ETB') === 'ETB') etbPaid += Number(r.total_amount ?? 0);
  });

  return {
    requestToApprovalHours: avg(reqToApp),
    approvalToPaidHours: avg(appToPaid),
    requestToPaidAvgHours: avg(reqToPaid),
    requestToPaidMedianHours: median(reqToPaid),
    etbPaid,
    paidCount: rows.length,
  };
};

const fetchPaid = async (from: string, to: string) => {
  const { data, error } = await supabase
    .from('payment_requests')
    .select('submitted_at, approved_at, paid_at, created_at, total_amount, currency')
    .eq('status', 'paid')
    .gte('paid_at', `${from}T00:00:00`)
    .lte('paid_at', `${to}T23:59:59`)
    .limit(10000);

  if (error) throw error;
  return (data ?? []) as unknown as Row[];
};

const shiftDays = (date: string, days: number) => {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export const usePaymentSpeedKPI = (dateFrom: string, dateTo: string) => {
  return useQuery({
    queryKey: ['payment-speed-kpi', dateFrom, dateTo],
    queryFn: async (): Promise<PaymentSpeedKPI> => {
      const spanDays =
        Math.round(
          (new Date(`${dateTo}T00:00:00`).getTime() - new Date(`${dateFrom}T00:00:00`).getTime()) /
            (24 * HOUR),
        ) + 1;

      const prevTo = shiftDays(dateFrom, -1);
      const prevFrom = shiftDays(prevTo, -(spanDays - 1));

      const [current, previous] = await Promise.all([
        fetchPaid(dateFrom, dateTo),
        fetchPaid(prevFrom, prevTo),
      ]);

      return {
        current: buildStats(current),
        previous: buildStats(previous),
        currentLabel: `${dateFrom} → ${dateTo}`,
        previousLabel: `${prevFrom} → ${prevTo}`,
      };
    },
  });
};
